import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { API_BASE_URL } from '../config';

function VerifyEmail() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const [status, setStatus] = useState('verifying'); // 'verifying', 'success', 'error'
  const [message, setMessage] = useState('');
  const [countdown, setCountdown] = useState(3);
  const [userRole, setUserRole] = useState(null); // Store user role for redirect

  useEffect(() => {
    const verifyEmail = async () => {
      const token = searchParams.get('token');

      if (!token) {
        setStatus('error');
        setMessage('Invalid verification link. No token provided.');
        return;
      }

      try {
        const response = await fetch(`${API_BASE_URL}/api/auth/verify-email`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ token }),
        });

        const data = await response.json();

        if (data.success) {
          setStatus('success');
          setMessage(data.message || 'Email verified successfully! You can now login.');
          // Store user role for redirect
          if (data.user && data.user.role) {
            setUserRole(data.user.role);
          }
        } else {
          setStatus('error');
          setMessage(data.error || 'Verification failed. Please try again.');
        }
      } catch (error) {
        setStatus('error');
        setMessage('Network error. Please check your connection and try again.');
        console.error('Verification error:', error);
      }
    };

    verifyEmail();
  }, [searchParams]);

  // Countdown and redirect on success
  useEffect(() => {
    if (status === 'success' && countdown > 0) {
      const timer = setTimeout(() => {
        setCountdown(countdown - 1);
      }, 1000);

      return () => clearTimeout(timer);
    } else if (status === 'success' && countdown === 0) {
      // Redirect to login with the appropriate mode based on user role
      if (userRole === 'doctor') {
        navigate('/?mode=doctor');
      } else if (userRole === 'patient') {
        navigate('/?mode=patient');
      } else {
        navigate('/');
      }
    }
  }, [status, countdown, navigate, userRole]);

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        {status === 'verifying' && (
          <div style={styles.content}>
            <div style={styles.spinner}></div>
            <h2 style={styles.title}>Verifying your email...</h2>
            <p style={styles.text}>Please wait while we verify your email address.</p>
          </div>
        )}

        {status === 'success' && (
          <div style={styles.content}>
            <div style={styles.successIcon}>✓</div>
            <h2 style={styles.title}>Email Verified!</h2>
            <p style={styles.text}>{message}</p>
            <p style={styles.countdown}>
              Redirecting to login in {countdown} second{countdown !== 1 ? 's' : ''}...
            </p>
            <button
              onClick={() => {
                if (userRole === 'doctor') {
                  navigate('/?mode=doctor');
                } else if (userRole === 'patient') {
                  navigate('/?mode=patient');
                } else {
                  navigate('/');
                }
              }}
              style={styles.button}
            >
              Go to Login Now
            </button>
          </div>
        )}

        {status === 'error' && (
          <div style={styles.content}>
            <div style={styles.errorIcon}>✕</div>
            <h2 style={styles.title}>Verification Failed</h2>
            <p style={styles.text}>{message}</p>
            <div style={styles.errorHelp}>
              <p style={styles.helpText}>Common reasons:</p>
              <ul style={styles.helpList}>
                <li>The verification link has expired (valid for 1 hour)</li>
                <li>The link has already been used</li>
                <li>The link is invalid or incomplete</li>
              </ul>
            </div>
            <button
              onClick={() => navigate('/')}
              style={styles.button}
            >
              Go to Login
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

const styles = {
  container: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    padding: '20px',
  },
  card: {
    backgroundColor: 'white',
    borderRadius: '12px',
    boxShadow: '0 10px 40px rgba(0, 0, 0, 0.1)',
    padding: '40px',
    maxWidth: '500px',
    width: '100%',
  },
  content: {
    textAlign: 'center',
  },
  title: {
    fontSize: '28px',
    fontWeight: '600',
    color: '#333',
    marginBottom: '16px',
    marginTop: '20px',
  },
  text: {
    fontSize: '16px',
    color: '#666',
    marginBottom: '20px',
    lineHeight: '1.6',
  },
  spinner: {
    width: '60px',
    height: '60px',
    margin: '0 auto',
    border: '4px solid #f3f3f3',
    borderTop: '4px solid #667eea',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
  },
  successIcon: {
    width: '80px',
    height: '80px',
    margin: '0 auto',
    backgroundColor: '#10b981',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '48px',
    color: 'white',
    fontWeight: 'bold',
  },
  errorIcon: {
    width: '80px',
    height: '80px',
    margin: '0 auto',
    backgroundColor: '#ef4444',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '48px',
    color: 'white',
    fontWeight: 'bold',
  },
  countdown: {
    fontSize: '14px',
    color: '#999',
    marginBottom: '20px',
  },
  button: {
    backgroundColor: '#667eea',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    padding: '12px 32px',
    fontSize: '16px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'background-color 0.3s',
    marginTop: '10px',
  },
  errorHelp: {
    backgroundColor: '#fef2f2',
    borderLeft: '4px solid #ef4444',
    padding: '16px',
    borderRadius: '4px',
    marginTop: '20px',
    marginBottom: '20px',
    textAlign: 'left',
  },
  helpText: {
    fontSize: '14px',
    fontWeight: '600',
    color: '#333',
    marginBottom: '8px',
  },
  helpList: {
    fontSize: '14px',
    color: '#666',
    paddingLeft: '20px',
    margin: '0',
  },
};

// Add CSS animation for spinner
const styleSheet = document.createElement("style");
styleSheet.textContent = `
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;
document.head.appendChild(styleSheet);

export default VerifyEmail;
